import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { SERVER_BASE_URL } from '../config';
import ConfirmModal from './ConfirmModal';

function WorkersSelector({ eventId }) {
  const [assignedWorkers, setAssignedWorkers] = useState([]);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [workerToRemove, setWorkerToRemove] = useState(null);

  // Для фикса "лишнего" открытия меню после удаления
  const ignoreNextOpen = useRef(false);

  const getToken = () => localStorage.getItem('token');
  const authFetch = async (endpoint, options = {}) => {
    const token = getToken();
    const response = await fetch(`${SERVER_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    return response;
  };

  const fetchAssignedWorkers = async () => {
    try {
      const res = await authFetch(`/api/events/${eventId}/workers`);
      if (res.ok) {
        setAssignedWorkers(await res.json());
      }
    } catch (err) {
      console.error('Error fetching assigned workers:', err);
    }
  };

  const fetchAvailableWorkers = async () => {
    try {
      const res = await authFetch('/api/users/my-workers');
      if (res.ok) {
        const data = await res.json();
        setAvailableWorkers(data.workers || []);
      }
    } catch (err) {
      console.error('Error fetching available workers:', err);
    }
  };

  const addWorker = async (workerId) => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/events/${eventId}/workers/${workerId}`, { method: 'POST' });
      if (res.ok) {
        await fetchAssignedWorkers();
        toast.success('Worker added!');
        setShowDropdown(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add worker');
      }
    } catch {
      toast.error('Error adding worker');
    }
    setLoading(false);
  };

  // Новый обработчик удаления с ConfirmModal
  const handleRemoveWorker = async () => {
    if (!workerToRemove) return;
    setLoading(true);
    try {
      const res = await authFetch(`/api/events/${eventId}/workers/${workerToRemove}`, { method: 'DELETE' });
      if (res.ok) {
        setAssignedWorkers(prev => prev.filter(w => w.id !== workerToRemove));
        toast.success('Worker removed!');
      } else {
        toast.error('Failed to remove worker');
        await fetchAssignedWorkers();
      }
    } catch {
      toast.error('Error removing worker');
      await fetchAssignedWorkers();
    }
    setLoading(false);
    setShowConfirm(false);
    setWorkerToRemove(null);
    ignoreNextOpen.current = true;
  };

  const getUnassignedWorkers = () => {
    const assignedIds = assignedWorkers.map(w => w.id);
    return availableWorkers.filter(w => !assignedIds.includes(w.id));
  };

  // Dropdown click-away
  useEffect(() => {
    if (!showDropdown) return;
    const onClick = (e) => {
      if (
        !e.target.closest('.workers-dropdown') &&
        !e.target.closest('.workers-plus-container')
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showDropdown]);

  useEffect(() => {
    fetchAssignedWorkers();
    fetchAvailableWorkers();
    // eslint-disable-next-line
  }, [eventId]);

  // Открытие дропдауна через плюс
  const handlePlusClick = (e) => {
    e.stopPropagation();
    if (ignoreNextOpen.current) {
      ignoreNextOpen.current = false;
      return;
    }
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="w-full">
      <div className="workers-row flex items-center gap-2 min-h-[42px]">
        <div
          className={`
            workers-field flex items-center flex-wrap gap-2 rounded-lg px-2 py-1
            focus-within:border-indigo-500 transition min-h-[38px] w-full relative
            max-w-[320px]
          `}
        >
          {assignedWorkers.length === 0 && (
            <span className="text-gray-400 italic pl-2">No workers assigned</span>
          )}
          {assignedWorkers.map(worker => (
            <span
              key={worker.id}
              className="flex items-center bg-white border border-gray-200 rounded-full px-3 py-1 text-sm shadow-sm"
            >
              <span className="mr-1 font-medium">{worker.name || worker.email}</span>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setWorkerToRemove(worker.id);
                  setShowConfirm(true);
                }}
                className="ml-1 text-gray-400 hover:text-red-500 focus:outline-none"
                disabled={loading}
                tabIndex={-1}
                title="Remove"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </span>
          ))}
          {/* Плюс справа, обёрнут в отдельный div для position: relative */}
          <div className="workers-plus-container relative ml-1">
            <button
              type="button"
              onClick={handlePlusClick}
              className={`
                w-8 h-8 flex items-center justify-center  border border-gray-200
                hover:bg-indigo-100 rounded-full text-indigo-600 transition shadow-sm
                disabled:opacity-40
              `}
              disabled={loading || getUnassignedWorkers().length === 0}
              title="Add worker"
              tabIndex={-1}
            >
              <PlusIcon className="w-5 h-5" />
            </button>
            {/* Выпадающее меню прямо под плюсом */}
            {showDropdown && (
              <div className="workers-dropdown absolute top-full right-0 mt-2 min-w-[220px] max-w-[320px] w-full bg-white rounded-lg shadow-lg border border-gray-200 z-20 animate-fadeIn">
                <div className="py-2 max-h-60 overflow-y-auto">
                  {getUnassignedWorkers().length === 0 ? (
                    <div className="px-4 py-2 text-gray-500 text-sm">
                      No available workers
                    </div>
                  ) : (
                    getUnassignedWorkers().map(worker => (
                      <button
                        key={worker.id}
                        type="button"
                        onClick={() => addWorker(worker.id)}
                        className="w-full text-left px-4 py-2 hover:bg-indigo-50 transition-colors flex flex-col items-start"
                        disabled={loading}
                      >
                        <span className="font-medium text-gray-900">{worker.name || 'Unnamed'}</span>
                        <span className="text-xs text-gray-500">{worker.email}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Модалка подтверждения */}
          <ConfirmModal
            isOpen={showConfirm}
            onClose={() => { setShowConfirm(false); setWorkerToRemove(null); }}
            onConfirm={handleRemoveWorker}
            message="Remove this worker from the event?"
          />
        </div>
      </div>
    </div>
  );
}

WorkersSelector.propTypes = {
  eventId: PropTypes.number.isRequired,
};

export default WorkersSelector;
